/**
 * Tenant Detail Page — Cycle 7 §4 보강.
 * SystemAdminPage 에서 tenant 클릭 시 진입. detail + unfreeze + quota 편집.
 *
 * Cycle 69: i18n 적용 (cycle 67 false negative 5건 중 본 page). 22 hardcoded → sysadmin-i18n.ts.
 */

import { useEffect, useState, useSyncExternalStore, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import {
  getTenantDetail, unfreezeTenant, getTenantQuota, updateTenantQuota,
  type TenantDetailResponse, type TenantQuota,
} from '../../api/systemAdmin';
// Cycle 43 — lang-aware date locale, Cycle 69 — sysadmin-i18n
import { formatDateTime } from '../../lib/base-i18n';
import { layerPrompt } from '../../components/LayerPopup/LayerPopup';
import { tSysadmin, getSysadminLang, subscribeSysadminLang, type SysadminI18nKey } from '../../lib/sysadmin-i18n';
import '../AdminConsolePage/AdminConsolePage.css';

export function TenantDetailPage() {
  useSyncExternalStore(subscribeSysadminLang, getSysadminLang, getSysadminLang);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const [detail, setDetail] = useState<TenantDetailResponse | null>(null);
  const [quota, setQuota] = useState<TenantQuota | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // quota form
  const [rateLimitInput, setRateLimitInput] = useState('');
  const [tokenLimitInput, setTokenLimitInput] = useState('');

  async function reload() {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [d, q] = await Promise.all([getTenantDetail(id), getTenantQuota(id)]);
      setDetail(d);
      setQuota(q);
      setRateLimitInput(q.rateLimitPerMin?.toString() ?? '');
      setTokenLimitInput(q.llmMaxTokensPerDay ?? '');
    } catch (e: any) {
      setError(e?.response?.data?.message ?? tSysadmin('sysadmin.tenant.err_load'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); }, [id]);

  async function handleUnfreeze() {
    if (!id) return;
    const reason = await layerPrompt(tSysadmin('sysadmin.tenant.unfreeze_prompt'));
    if (!reason || reason.trim().length < 10) {
      setError(tSysadmin('sysadmin.tenant.err_reason_too_short'));
      return;
    }
    try {
      await unfreezeTenant(id, { reason: reason.trim() });
      void reload();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? tSysadmin('sysadmin.tenant.err_unfreeze'));
    }
  }

  async function handleQuotaSave(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setError(null);
    try {
      await updateTenantQuota(id, {
        rateLimitPerMin: rateLimitInput.trim() === '' ? null : Number(rateLimitInput),
        llmMaxTokensPerDay: tokenLimitInput.trim() === '' ? null : Number(tokenLimitInput),
      });
      void reload();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? tSysadmin('sysadmin.tenant.err_quota_save'));
    }
  }

  if (!user || user.role !== 'SYSTEM_ADMIN') {
    return (
      <div className="admin-console admin-console--denied" role="alert">
        <h2>{tSysadmin('sysadmin.access_denied')}</h2>
      </div>
    );
  }

  if (loading) return <div className="admin-console"><main className="admin-console__content">{tSysadmin('sysadmin.loading')}</main></div>;
  if (!detail || !quota) return <div className="admin-console"><main className="admin-console__content">{tSysadmin('sysadmin.tenant.not_found')}</main></div>;

  const t = detail.tenant;

  return (
    <div className="admin-console">
      <aside className="admin-console__sidebar">
        <button
          type="button"
          className="admin-console__btn"
          onClick={() => navigate('/system-admin')}
          style={{ width: '100%', marginBottom: '1rem' }}
        >
          {tSysadmin('sysadmin.tenant.back_to_list')}
        </button>
      </aside>

      <main className="admin-console__content">
        <h2 className="admin-console__heading">{t.name}</h2>
        <p className="admin-console__subheading">
          <code>{t.code}</code> · {t.domain ?? tSysadmin('sysadmin.tenant.no_domain')}
        </p>

        {error && <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>}

        <section style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginTop: 0 }}>{tSysadmin('sysadmin.tenant.status_section_heading')}</h3>
          <table className="admin-console__table">
            <tbody>
              {/* Cycle 74 — status raw 출력 → 현지화 라벨 매핑 (transit-state raw 출력 잔여 봉합) */}
              <tr><th>{tSysadmin('sysadmin.tenant.col.status')}</th><td>{tSysadmin(`sysadmin.tenant.status_value.${t.status}` as SysadminI18nKey)}</td></tr>
              <tr><th>{tSysadmin('sysadmin.tenant.col.created_at')}</th><td>{formatDateTime(t.createdAt)}</td></tr>
              <tr><th>{tSysadmin('sysadmin.tenant.col.verified_at')}</th><td>{t.verifiedAt ? formatDateTime(t.verifiedAt) : '—'}</td></tr>
              <tr><th>{tSysadmin('sysadmin.tenant.col.verification_method')}</th><td>{t.verificationMethod ?? '—'}</td></tr>
              <tr><th>{tSysadmin('sysadmin.tenant.col.grace')}</th><td>{t.graceActive ? tSysadmin('sysadmin.tenant.grace_active', { until: t.graceUntil ? formatDateTime(t.graceUntil) : '' }) : tSysadmin('sysadmin.tenant.grace_done')}</td></tr>
              <tr><th>{tSysadmin('sysadmin.tenant.col.frozen')}</th><td>{t.frozen ? tSysadmin('sysadmin.tenant.frozen_at', { when: t.frozenAt ? formatDateTime(t.frozenAt) : '' }) : tSysadmin('sysadmin.tenant.frozen_no')}</td></tr>
              <tr><th>{tSysadmin('sysadmin.tenant.col.users')}</th><td>{detail.counts.users}</td></tr>
              <tr><th>{tSysadmin('sysadmin.tenant.col.llm_integrations')}</th><td>{detail.counts.llmIntegrations} {tSysadmin('sysadmin.tenant.llm_unit')}</td></tr>
            </tbody>
          </table>
          {t.frozen && (
            <button
              type="button"
              className="admin-console__btn admin-console__btn--primary"
              style={{ marginTop: '0.75rem' }}
              onClick={() => void handleUnfreeze()}
            >
              {tSysadmin('sysadmin.tenant.btn_unfreeze')}
            </button>
          )}
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h3>{tSysadmin('sysadmin.tenant.quota_heading')}</h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary, #6b7280)' }}>
            {tSysadmin('sysadmin.tenant.quota_hint')}
          </p>
          <form onSubmit={(e) => void handleQuotaSave(e)}>
            <div className="admin-console__form-row">
              <label htmlFor="rateLimit">{tSysadmin('sysadmin.tenant.quota.label_rate_limit')}</label>
              <input
                id="rateLimit"
                type="number"
                min="1"
                max="100000"
                className="admin-console__input"
                value={rateLimitInput}
                onChange={(e) => setRateLimitInput(e.target.value)}
                placeholder={tSysadmin('sysadmin.tenant.quota.placeholder_rate')}
              />
            </div>
            <div className="admin-console__form-row">
              <label htmlFor="tokenLimit">{tSysadmin('sysadmin.tenant.quota.label_token_limit')}</label>
              <input
                id="tokenLimit"
                type="number"
                min="0"
                className="admin-console__input"
                value={tokenLimitInput}
                onChange={(e) => setTokenLimitInput(e.target.value)}
                placeholder={tSysadmin('sysadmin.tenant.quota.placeholder_token')}
              />
            </div>
            <button type="submit" className="admin-console__btn admin-console__btn--primary">
              {tSysadmin('sysadmin.tenant.quota.btn_save')}
            </button>
          </form>
        </section>

        <section>
          <h3>{tSysadmin('sysadmin.tenant.audit_heading')}</h3>
          {detail.recentAudits.length === 0 ? (
            <p>{tSysadmin('sysadmin.tenant.audit_empty')}</p>
          ) : (
            <table className="admin-console__table">
              <thead><tr><th>{tSysadmin('sysadmin.tenant.audit.col.time')}</th><th>{tSysadmin('sysadmin.tenant.audit.col.event')}</th><th>{tSysadmin('sysadmin.tenant.audit.col.actor')}</th></tr></thead>
              <tbody>
                {detail.recentAudits.map((a) => (
                  <tr key={a.id}>
                    <td style={{ fontSize: '0.8125rem' }}>{formatDateTime(a.ts)}</td>
                    <td><code style={{ fontSize: '0.8125rem' }}>{a.eventType}</code></td>
                    <td>{a.actorId ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </main>
    </div>
  );
}
